import api from './api';

const planchetteIntelService = {
  // --- Entities ---
  getEntities: async (params = {}) => {
    const response = await api.get('/planchette-intel/entities', { params });
    return response.data;
  },

  getEntity: async (entityId) => {
    const response = await api.get(`/planchette-intel/entities/${entityId}`);
    return response.data;
  },

  createEntity: async (data) => {
    const response = await api.post('/planchette-intel/entities', data);
    return response.data;
  },

  updateEntity: async (entityId, data) => {
    const response = await api.put(`/planchette-intel/entities/${entityId}`, data);
    return response.data;
  },

  linkContact: async (entityId, contactId) => {
    const response = await api.post(`/planchette-intel/entities/${entityId}/link-contact`, { contact_id: contactId });
    return response.data;
  },

  createEdge: async (data) => {
    const response = await api.post('/planchette-intel/edges', data);
    return response.data;
  },

  // --- Graph ---
  graphTraverse: async (entityId, depth = 3, relTypes = null) => {
    const params = { entity_id: entityId, depth };
    if (relTypes) params.rel_types = relTypes.join(',');
    const response = await api.get('/planchette-intel/graph/traverse', { params });
    return response.data;
  },

  graphPath: async (fromId, toId, maxDepth = 6) => {
    const response = await api.get('/planchette-intel/graph/path', { params: { from_id: fromId, to_id: toId, max_depth: maxDepth } });
    return response.data;
  },

  graphCommon: async (entityA, entityB) => {
    const response = await api.get('/planchette-intel/graph/common', { params: { entity_a: entityA, entity_b: entityB } });
    return response.data;
  },

  propertyChain: async (personId) => {
    const response = await api.get(`/planchette-intel/graph/property-chain/${personId}`);
    return response.data;
  },

  graphVisualization: async (entityId, depth = 2) => {
    const response = await api.get(`/planchette-intel/graph/visualization/${entityId}`, { params: { depth } });
    return response.data;
  },

  // --- Enrichment ---
  enrich: async (data) => {
    const response = await api.post('/planchette-intel/enrich', data);
    return response.data;
  },

  enrichCase: async (caseId) => {
    const response = await api.post(`/planchette-intel/enrich/case/${caseId}`);
    return response.data;
  },

  getEnrichmentJobs: async (params = {}) => {
    const response = await api.get('/planchette-intel/enrich/jobs', { params });
    return response.data;
  },

  getEnrichmentJob: async (jobId) => {
    const response = await api.get(`/planchette-intel/enrich/jobs/${jobId}`);
    return response.data;
  },

  // --- Findings ---
  getFindings: async (params = {}) => {
    const response = await api.get('/planchette-intel/findings', { params });
    return response.data;
  },

  getFinding: async (findingId) => {
    const response = await api.get(`/planchette-intel/findings/${findingId}`);
    return response.data;
  },

  reviewFinding: async (findingId, reviewNotes = null) => {
    const response = await api.put(`/planchette-intel/findings/${findingId}/review`, { review_notes: reviewNotes });
    return response.data;
  },

  // --- Watches ---
  getWatches: async (params = {}) => {
    const response = await api.get('/planchette-intel/watches', { params });
    return response.data;
  },

  createWatch: async (data) => {
    const response = await api.post('/planchette-intel/watches', data);
    return response.data;
  },

  deleteWatch: async (watchId) => {
    const response = await api.delete(`/planchette-intel/watches/${watchId}`);
    return response.data;
  },

  // --- Alerts ---
  getAlerts: async (params = {}) => {
    const response = await api.get('/planchette-intel/alerts', { params });
    return response.data;
  },

  markAlertRead: async (alertId) => {
    const response = await api.put(`/planchette-intel/alerts/${alertId}/read`);
    return response.data;
  },

  // --- Resolution ---
  resolve: async (personA, personB) => {
    const response = await api.post('/planchette-intel/resolve', { person_a: personA, person_b: personB });
    return response.data;
  },

  getPendingResolutions: async (params = {}) => {
    const response = await api.get('/planchette-intel/resolve/pending', { params });
    return response.data;
  },

  resolveAction: async (logId, action) => {
    const response = await api.post(`/planchette-intel/resolve/${logId}/action`, { action });
    return response.data;
  },

  // --- Stats ---
  getStats: async () => {
    const response = await api.get('/planchette-intel/stats');
    return response.data;
  },
  downloadReport: (entityId, depth = 2) => {
    return api.get(`/planchette-intel/report/${entityId}?depth=${depth}`, {
      responseType: 'blob',
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `planchette_intel_dossier_${entityId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  },
  // --- Email Collector ---
  getEmailCollectorStatus: async () => {
    const response = await api.get('/planchette-intel/collectors/email/status');
    return response.data;
  },
  getEmailCollectorHistory: async (params = {}) => {
    const response = await api.get('/planchette-intel/collectors/email/history', { params });
    return response.data;
  },
  triggerEmailPoll: async () => {
    const response = await api.post('/planchette-intel/collectors/email/poll');
    return response.data;
  },
};

export default planchetteIntelService;
